import React, { ReactNode, useEffect, useRef, useState } from 'react';
import './NewUserItem.scss';

import './AddUserModal.scss';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	DropDownGold,
	CheckmarkIcon,
	IconWarningFilled
} from '@/assets/images';
import { userAddNewUserRequest } from '@/modules/user/userActions';
import { sendAnalytics } from '@/services/analyticsService';
import type { RootState } from '@/rootReducer';
import type { User } from '../userTypes';

type Props = {
	newUserArray: any;
	setNewUserArray: any;
	closeModal: any;
	userToggleAddUserModal: any;
	changeTab: any;
	user: User;
};

export default function NewUserItem({
	setNewUserArray,
	newUserArray,
	closeModal,
	userToggleAddUserModal,
	changeTab,
	user
}: Props): JSX.Element {
	const [addUserLoading, addUserSuccess] = useSelector<RootState, [any, any]>(
		(state: RootState) => [
			state.user.getIn(['addUser', 'loading']),
			state.user.getIn(['addUser', 'success'])
		],
		shallowEqual
	);

	const [showEdit, setShowEdit] = useState<boolean>(false);
	const [userDetails, setUserDetails] = useState<object>();
	const [showValidations, setShowValidations] = useState<boolean>(false);
	const [specificErrorState, setSpecificError] = useState({});
	const [userIndex, setUserIndex] = useState();
	const [sentReport, setSentReport] = useState<boolean>(false);

	const currentUserFirstName = useRef<HTMLInputElement>(null);
	const currentUserLastName = useRef<HTMLInputElement>(null);
	const currentUserTitle = useRef<HTMLInputElement>(null);
	const currentUserEmailAddress = useRef<HTMLInputElement>(null);
	const currentUserRole = useRef<HTMLSelectElement>(null);
	const currentUserDepartment = useRef<HTMLSelectElement>(null);
	const currentUserNotes = useRef<HTMLSelectElement>(null);

	const dispatch = useDispatch();
	const addNewUserRequest = payload =>
		dispatch(userAddNewUserRequest({ data: payload }));

	const showUserDetails = index => {
		setUserIndex(index);
		setShowEdit(true);
		setUserDetails(newUserArray[index]);
		setScrollToTop();
	};

	const setScrollToTop = () => {
		setTimeout(() => {
			const scrollEl = document.getElementsByClassName('addUserModal')[0];
			if (scrollEl) {
				scrollEl.scrollTop = 0;
			}
		}, 15);
	};

	const regexEmail = new RegExp('[a-z0-9]+@[a-z0-9]+\.[a-z0-9]{2,2}');

	const checkUserArrayValid = () => {
		let valid = true;
		newUserArray.forEach(user => {
			if (user.errors === true || user.errors === null) {
				valid = false;
			}
		});
		return valid;
	};

	const closeUserDetails = validate => {
		setShowValidations(false);
		const userDetailsObject = {
			userNumber: userDetails.userNumber,
			firstName: currentUserFirstName.current.value,
			lastName: currentUserLastName.current.value,
			emailAddress: currentUserEmailAddress.current.value.toLowerCase(),
			title: currentUserTitle.current.value,
			role: currentUserRole.current.value,
			department: currentUserDepartment.current.value,
			userNotes: currentUserNotes.current.value,
			errors: true
		};

		if (validate) {
			let hasErrors = false;
			for (const item in userDetailsObject) {
				if (
					((item == 'firstName' ||
						item == 'lastName' ||
						item == 'role' ||
						item == 'emailAddress') &&
						userDetailsObject[item] === '') ||
					(item == 'emailAddress' &&
						!regexEmail.test(userDetailsObject[item]))
				) {
					const tempErrors = specificErrorState;
					tempErrors[item] = true;
					setSpecificError(...[tempErrors]);
					userDetailsObject.errors = true;
					hasErrors = true;
				} else {
					userDetails[item] = userDetailsObject[item];
					const tempErrors = specificErrorState;
					tempErrors[item] = false;
					setSpecificError(...[tempErrors]);
				}
			}

			const tempUserArray = newUserArray;
			tempUserArray[userIndex] = userDetailsObject;
			setNewUserArray([...tempUserArray]);

			if (!hasErrors) {
				userDetailsObject.errors = false;
				const tempUserArray = newUserArray;
				tempUserArray[userIndex] = userDetailsObject;
				setNewUserArray([...tempUserArray]);
				setShowValidations(false);
				setShowEdit(false);
				if (newUserArray.length == 1) {
					saveCSV(newUserArray);
				}

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `User pressed 'done' key for a User - no errors`,
						properties: {
							Fields: userDetailsObject
						}
					});
				}
			} else {
				setShowValidations(true);

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `User pressed 'done' key for a User - there are errors`,
						properties: {
							Fields: userDetailsObject
						}
					});
				}
			}
		} else {
			const tempUserArray = newUserArray;
			tempUserArray[userIndex] = userDetailsObject;
			tempUserArray[userIndex].errors = false;
			for (const item in tempUserArray[userIndex]) {
				if (
					((item == 'firstName' ||
						item == 'lastName' ||
						item == 'role' ||
						item == 'emailAddress') &&
						tempUserArray[userIndex][item] === '') ||
					(item == 'emailAddress' &&
						!regexEmail.test(tempUserArray[userIndex][item]))
				) {
					tempUserArray[userIndex].errors = true;
				}
			}
			setNewUserArray([...tempUserArray]);
			setShowEdit(false);

			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `User pressed 'back' key for a User`,
					properties: {
						Fields: userDetailsObject
					}
				});
			}
		}
	};

	useEffect(() => {
		if (addUserSuccess === true) {
			setSentReport(true);
		}
	}, [addUserSuccess]);

	const saveCSV = array => {
		if (addUserLoading) {
			return;
		}

		const valid = checkUserArrayValid();
		if (!valid) {
			return;
		}

		addNewUserRequest(array);
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User submitted Add New User Request`,
				properties: {
					Items: array
				}
			});
		}
	};

	const backUserSelect = newUserArray => {
		const originalLength = newUserArray.length;
		changeTab('initial');
		return { newUserArray, originalLength };
	};

	const openDropdown = el => {
		el.classList.add('dropdown-open');
	};

	const closeDropdown = el => {
		el.classList.remove('dropdown-open');
	};

	const toggleDropdown = el => {
		if (el.classList.contains('dropdown-open')) {
			closeDropdown(el);
		} else {
			openDropdown(el);
		}
	};

	const selectDropdownItem = (input, value) => {
		input['value'] = value;
	};

	return (
		<div>
			{showEdit === false &&
				sentReport === false &&
				newUserArray.length > 1 && (
					<div className="allUserScreen">
						Enter the new user details to add them to your Resquared
						account
						{newUserArray.map((user: any, index: any) => {
							return (
								<>
									<div
										key={Math.random()}
										className={`userButtons ${user.errors === true || user.errors === false ? 'user-filled' : ''}`}
										onClick={() => showUserDetails(index)}
									>
										{(user.firstName === '' ||
											user.lastName === '') && (
											<div
												className="userNameClick"
												onClick={() =>
													showUserDetails(index)
												}
											>
												{user.userNumber}
											</div>
										)}
										{user.firstName != '' &&
											user.lastName != '' &&
											user.role == null && (
												<div
													className="userNameClick"
													onClick={() =>
														showUserDetails(index)
													}
												>
													{user.firstName}{' '}
													{user.lastName}
												</div>
											)}
										{user.firstName != '' &&
											user.lastName != '' &&
											user.role != null && (
												<div
													className="userNameClick"
													onClick={() =>
														showUserDetails(index)
													}
												>
													{user.firstName}{' '}
													{user.lastName}
													<div className="show-role">
														{user.role}
													</div>
												</div>
											)}
										{user.errors === false && (
											<div className="completed-user">
												<CheckmarkIcon className="checkmark-icon" />
												Complete
											</div>
										)}
										{user.errors === true && (
											<div className="completed-user incomplete">
												Incomplete
											</div>
										)}
										<DropDownGold
											className="right-chevron"
											onClick={() =>
												showUserDetails(index)
											}
										/>
									</div>
								</>
							);
						})}
						<div className="bottom-buttons">
							<button
								type="submit"
								className="cancel-button"
								onClick={() => backUserSelect(newUserArray)}
							>
								Back
							</button>
							<button
								className={`submit-button ${checkUserArrayValid() ? '' : 'submit-button-disabled'}`}
								type="submit"
								onClick={() => saveCSV(newUserArray)}
							>
								Add new users
							</button>
						</div>
					</div>
				)}
			{newUserArray.length == 1 && showEdit === false && (
				<>{showUserDetails(0)}</>
			)}
			{showEdit === true &&
				sentReport === false &&
				newUserArray.length == 1 && (
					<>
						<div className="allUserScreen">
							<div>
								Enter the new user details to add them to your
								Resquared account
							</div>
							<div className="form-holder">
								<div className="form-item">
									<div className="form-item-title">
										First Name
									</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<input
											type="firstName"
											ref={el => {
												el?.focus();
												(currentUserFirstName.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter First Name"
											defaultValue={
												userDetails.firstName ?? ''
											}
										/>
										{showValidations &&
											specificErrorState.firstName && (
												<>
													{currentUserFirstName
														.current.value ===
														'' && (
														<div className="validationError">
															<IconWarningFilled className="validation-icon" />
															This field is
															required
														</div>
													)}
												</>
											)}
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">
										Last Name
									</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<input
											type="lastName"
											ref={el => {
												el?.focus();
												(currentUserLastName.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter Last Name"
											defaultValue={
												userDetails.lastName ?? ''
											}
										/>
										{showValidations &&
											specificErrorState.lastName && (
												<>
													{currentUserLastName.current
														.value === '' && (
														<div className="validationError">
															<IconWarningFilled className="validation-icon" />
															This field is
															required
														</div>
													)}
												</>
											)}
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">Title</div>
									<div className="form-input-wrapper">
										<input
											type="title"
											ref={el => {
												el?.focus();
												(currentUserTitle.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter Title"
											defaultValue={
												userDetails.title ?? ''
											}
											onBlur={evt => {
												if (
													evt.currentTarget.value
														.length > 0 &&
													user &&
													user.email
												) {
													sendAnalytics({
														type: 'track',
														userId: user.email,
														eventName: `User entered a Title (unrequired field) on Add User Modal`,
														properties: {
															note: evt
																.currentTarget
																.value
														}
													});
												}
											}}
										/>
									</div>
									<div className="bottom-border" />
								</div>

								<div className="form-item">
									<div className="form-item-title">
										Department
									</div>
									<div className="form-input-wrapper">
										<div className="input-field-dropdown-holder">
											<input
												className="input-field-dropdown"
												id="departmentList"
												defaultValue={
													userDetails?.department
												}
												placeholder="Select your Department"
												onClick={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
													const holder =
														evt.currentTarget
															.parentNode;
													toggleDropdown(
														holder,
														'Department'
													);
												}}
												onFocus={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
												}}
												ref={el => {
													el?.focus();
													(currentUserDepartment.current as ReactNode) =
														el;
												}}
											/>
											<div
												className="input-field-dropddown-bg"
												onClick={evt => {
													evt.stopPropagation();
													const holder =
														evt.currentTarget
															.parentNode;
													closeDropdown(holder);
												}}
											/>
											<div className="input-field-dropdown-options-holder">
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															null
														);
														closeDropdown(holder);
													}}
												>
													Select a department
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Sales'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Sales'
																}
															});
														}
													}}
												>
													Sales
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Marketing'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Marketing'
																}
															});
														}
													}}
												>
													Marketing
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Accounting'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Accounting'
																}
															});
														}
													}}
												>
													Accounting
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Management'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Management'
																}
															});
														}
													}}
												>
													Management
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Engineering'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Engineering'
																}
															});
														}
													}}
												>
													Engineering
												</div>
											</div>
										</div>
									</div>
									<div className="bottom-border" />
								</div>

								<div className="form-item">
									<div className="form-item-title">
										Email Address
									</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<input
											type="emailAddress"
											ref={el => {
												el?.focus();
												(currentUserEmailAddress.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter Email Address"
											defaultValue={
												userDetails.emailAddress ?? ''
											}
										/>
										<>
											{showValidations &&
												specificErrorState.emailAddress && (
													<>
														{!regexEmail.test(
															currentUserEmailAddress
																.current.value
														) &&
															currentUserEmailAddress
																.current
																.value !=
																'' && (
																<div className="validationError">
																	<IconWarningFilled className="validation-icon" />
																	Email must
																	be formatted
																	correctly
																</div>
															)}
														{currentUserEmailAddress
															.current.value ===
															'' && (
															<div className="validationError">
																<IconWarningFilled className="validation-icon" />
																This field is
																required
															</div>
														)}
													</>
												)}
										</>
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">Role</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<div className="input-field-dropdown-holder">
											<input
												className="input-field-dropdown"
												id="roleList"
												defaultValue={userDetails?.role}
												placeholder="Select your Role"
												onClick={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
													const holder =
														evt.currentTarget
															.parentNode;
													toggleDropdown(holder);
												}}
												onFocus={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
												}}
												ref={el => {
													el?.focus();
													(currentUserRole.current as ReactNode) =
														el;
												}}
											/>
											<div
												className="input-field-dropddown-bg"
												onClick={evt => {
													evt.stopPropagation();
													const holder =
														evt.currentTarget
															.parentNode;
													closeDropdown(holder);
												}}
											/>
											<div className="input-field-dropdown-options-holder">
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															null
														);
														closeDropdown(holder);
													}}
												>
													Select a Role
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															'User'
														);
														closeDropdown(holder);
													}}
												>
													User
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															'Manager'
														);
														closeDropdown(holder);
													}}
												>
													Manager
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															'Administrator'
														);
														closeDropdown(holder);
													}}
												>
													Administrator
												</div>
											</div>
										</div>
										<>
											{showValidations &&
												specificErrorState.role && (
													<>
														{currentUserRole.current
															.value === '' && (
															<div className="validationError">
																<IconWarningFilled className="validation-icon" />
																This field is
																required
															</div>
														)}
													</>
												)}
										</>
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">
										Notes on Markets and Locations
									</div>
									<div className="form-item-subtitle">
										Provide any additional information for
										your request
									</div>
									<div className="form-input-wrapper">
										<input
											type="title"
											ref={el => {
												el?.focus();
												(currentUserNotes.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter Market and Location Notes"
											defaultValue={
												userDetails.userNotes ?? ''
											}
											onBlur={evt => {
												if (
													evt.currentTarget.value
														.length > 0 &&
													user &&
													user.email
												) {
													sendAnalytics({
														type: 'track',
														userId: user.email,
														eventName: `User wrote notes (unrequired field) on Add User Modal`,
														properties: {
															note: evt
																.currentTarget
																.value
														}
													});
												}
											}}
										/>
									</div>
									<div className="bottom-border" />
								</div>
							</div>

							<div className="form-item-asterisk">*</div>
							<div className="required-indicator required-indicator-bottom">
								Required
							</div>

							<div className="bottom-buttons-single">
								<button
									type="submit"
									className="cancel-button"
									onClick={() => backUserSelect(newUserArray)}
								>
									Back
								</button>
								<button
									className="submit-button"
									type="submit"
									onClick={() => closeUserDetails(true)}
								>
									Add New User
								</button>
							</div>
						</div>
					</>
				)}
			{showEdit === true &&
				sentReport === false &&
				newUserArray.length != 1 && (
					<>
						<div className="allUserScreen">
							<DropDownGold
								className="back-chevron"
								onClick={() => closeUserDetails(false)}
							/>
							<div
								className="details-back-button"
								onClick={() => closeUserDetails(false)}
							>
								Details
							</div>
							<div className="userNumber">
								{userDetails.userNumber}
							</div>
							<div className="form-holder">
								<div className="form-item">
									<div className="form-item-title">
										First Name
									</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<input
											type="firstName"
											ref={el => {
												el?.focus();
												(currentUserFirstName.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter First Name"
											defaultValue={
												userDetails.firstName ?? ''
											}
										/>
										{showValidations &&
											specificErrorState.firstName && (
												<>
													{currentUserFirstName
														.current.value ===
														'' && (
														<div className="validationError">
															<IconWarningFilled className="validation-icon" />
															This field is
															required
														</div>
													)}
												</>
											)}
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">
										Last Name
									</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<input
											type="lastName"
											ref={el => {
												el?.focus();
												(currentUserLastName.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter Last Name"
											defaultValue={
												userDetails.lastName ?? ''
											}
										/>
										{showValidations &&
											specificErrorState.lastName && (
												<>
													{currentUserLastName.current
														.value === '' && (
														<div className="validationError">
															<IconWarningFilled className="validation-icon" />
															This field is
															required
														</div>
													)}
												</>
											)}
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">Title</div>
									<div className="form-input-wrapper">
										<input
											type="title"
											ref={el => {
												el?.focus();
												(currentUserTitle.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter Title"
											defaultValue={
												userDetails.title ?? ''
											}
											onBlur={evt => {
												if (
													evt.currentTarget.value
														.length > 0 &&
													user &&
													user.email
												) {
													sendAnalytics({
														type: 'track',
														userId: user.email,
														eventName: `User entered a Title (unrequired field) on Add User Modal`,
														properties: {
															note: evt
																.currentTarget
																.value
														}
													});
												}
											}}
										/>
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">
										Department
									</div>
									<div className="form-input-wrapper">
										<div className="input-field-dropdown-holder">
											<input
												className="input-field-dropdown"
												id="departmentList"
												defaultValue={
													userDetails?.department
												}
												placeholder="Select your Department"
												onClick={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
													const holder =
														evt.currentTarget
															.parentNode;
													toggleDropdown(
														holder,
														'Department'
													);
												}}
												onFocus={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
												}}
												ref={el => {
													el?.focus();
													(currentUserDepartment.current as ReactNode) =
														el;
												}}
											/>
											<div
												className="input-field-dropddown-bg"
												onClick={evt => {
													evt.stopPropagation();
													const holder =
														evt.currentTarget
															.parentNode;
													closeDropdown(holder);
												}}
											/>
											<div className="input-field-dropdown-options-holder">
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															null
														);
														closeDropdown(holder);
													}}
												>
													Select a department
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Sales'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Sales'
																}
															});
														}
													}}
												>
													Sales
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Marketing'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Marketing'
																}
															});
														}
													}}
												>
													Marketing
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Accounting'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Accounting'
																}
															});
														}
													}}
												>
													Accounting
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Management'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Management'
																}
															});
														}
													}}
												>
													Management
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#departmentList'
															);
														selectDropdownItem(
															input,
															'Engineering'
														);
														closeDropdown(holder);
														if (
															user &&
															user.email
														) {
															sendAnalytics({
																type: 'track',
																userId: user.email,
																eventName: `User selected a department (unrequired field) on Add User Modal`,
																properties: {
																	department:
																		'Engineering'
																}
															});
														}
													}}
												>
													Engineering
												</div>
											</div>
										</div>
									</div>
									<div className="bottom-border" />
								</div>

								<div className="form-item">
									<div className="form-item-title">
										Email Address
									</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<input
											type="emailAddress"
											ref={el => {
												el?.focus();
												(currentUserEmailAddress.current as ReactNode) =
													el;
											}}
											className="input-field"
											placeholder="Enter Email Address"
											defaultValue={
												userDetails.emailAddress ?? ''
											}
										/>
										<>
											{showValidations &&
												specificErrorState.emailAddress && (
													<>
														{!regexEmail.test(
															currentUserEmailAddress
																.current.value
														) &&
															currentUserEmailAddress
																.current
																.value !=
																'' && (
																<div className="validationError">
																	<IconWarningFilled className="validation-icon" />
																	Email must
																	be formatted
																	correctly
																</div>
															)}
														{currentUserEmailAddress
															.current.value ===
															'' && (
															<div className="validationError">
																<IconWarningFilled className="validation-icon" />
																This field is
																required
															</div>
														)}
													</>
												)}
										</>
									</div>
									<div className="bottom-border" />
								</div>
								<div className="form-item">
									<div className="form-item-title">Role</div>
									<div className="form-item-asterisk">*</div>
									<div className="form-input-wrapper">
										<div className="input-field-dropdown-holder">
											<input
												className="input-field-dropdown"
												id="roleList"
												defaultValue={userDetails?.role}
												placeholder="Select your Role"
												onClick={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
													const holder =
														evt.currentTarget
															.parentNode;
													toggleDropdown(holder);
												}}
												onFocus={evt => {
													evt.stopPropagation();
													evt.currentTarget.blur();
												}}
												ref={el => {
													el?.focus();
													(currentUserRole.current as ReactNode) =
														el;
												}}
											/>
											<div
												className="input-field-dropddown-bg"
												onClick={evt => {
													evt.stopPropagation();
													const holder =
														evt.currentTarget
															.parentNode;
													closeDropdown(holder);
												}}
											/>
											<div className="input-field-dropdown-options-holder">
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															null
														);
														closeDropdown(holder);
													}}
												>
													Select a Role
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															'User'
														);
														closeDropdown(holder);
													}}
												>
													User
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															'Manager'
														);
														closeDropdown(holder);
													}}
												>
													Manager
												</div>
												<div
													className="input-field-dropdown-option"
													onClick={evt => {
														evt.stopPropagation();
														const holder =
															evt.currentTarget
																.parentNode
																.parentNode;
														const input =
															evt.currentTarget.parentNode.parentNode.querySelector(
																'#roleList'
															);
														selectDropdownItem(
															input,
															'Administrator'
														);
														closeDropdown(holder);
													}}
												>
													Administrator
												</div>
											</div>
										</div>
										<>
											{showValidations &&
												specificErrorState.role && (
													<>
														{currentUserRole.current
															.value === '' && (
															<div className="validationError">
																<IconWarningFilled className="validation-icon" />
																This field is
																required
															</div>
														)}
													</>
												)}
										</>
									</div>
									<div className="bottom-border" />
								</div>
							</div>
							<div className="form-item">
								<div className="form-item-title">
									Notes on Markets and Locations
								</div>
								<div className="form-item-subtitle">
									Provide any additional information for your
									request
								</div>
								<div className="form-input-wrapper">
									<input
										type="title"
										ref={el => {
											el?.focus();
											(currentUserNotes.current as ReactNode) =
												el;
										}}
										className="input-field"
										placeholder="Enter Market and Location Notes"
										defaultValue={
											userDetails.userNotes ?? ''
										}
										onBlur={evt => {
											if (
												evt.currentTarget.value.length >
													0 &&
												user &&
												user.email
											) {
												sendAnalytics({
													type: 'track',
													userId: user.email,
													eventName: `User wrote notes (unrequired field) on Add User Modal`,
													properties: {
														note: evt.currentTarget
															.value
													}
												});
											}
										}}
									/>
								</div>
								<div className="bottom-border" />
							</div>

							<div className="form-item-asterisk">*</div>
							<div className="required-indicator required-indicator-bottom">
								Required
							</div>

							<div className="bottom-buttons-single">
								<button
									type="submit"
									className="cancel-button"
									onClick={() => closeUserDetails(false)}
								>
									Back
								</button>
								<button
									className="done-button"
									type="submit"
									onClick={() => closeUserDetails(true)}
								>
									Done
								</button>
							</div>
						</div>
					</>
				)}

			{sentReport === true && addUserSuccess === true && (
				<div className="done-screen-holder">
					<CheckmarkIcon className="success-checkmark" />
					<div className="done-screen">
						Success! New user will be added to your bill
					</div>
					<div className="done-screen-paragraph">
						<span>
							We will confirm via email your updated billing and
							send invitations to new users
						</span>
					</div>
					<div className="done-screen-button-wrapper">
						<button
							type="submit"
							className="done-screen-button"
							onClick={() => {
								closeModal();
							}}
						>
							Done
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
